// src/modules/profile/components/ProfileView/index.js
import React, { useState } from 'react';
import { Card, Button, Image, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CompetitionsWonCard from '../CompetitionsWonCard';
import CurrentCompetitionsCard from '../CurrentCompetitionsCard';
import StravaConnectCard from '../StravaConnectCard';
import PermissionsCard from '../PermissionsCard';
import RecentActivities from '../../../dashboard/components/RecentActivities';

import './styles.css';

const stravaEnabled = process.env.REACT_APP_FEATURE_STRAVA === 'true';

const StatItem = ({ label, value, unit }) => (
    <div className="stat-item">
        <span className="stat-label">{label}</span>
        <span className="stat-value">
            {typeof value === 'number' ? value.toFixed(1) : value}
            {unit && <span className="stat-unit">{unit}</span>}
        </span>
    </div>
);

const ACTIVITY_TYPE_CONFIG = {
    running: { icon: 'bi-person-walking', color: '#dc3545', label: 'Running' },
    cycling: { icon: 'bi-bicycle', color: '#0d6efd', label: 'Cycling' },
    swimming: { icon: 'bi-water', color: '#0dcaf0', label: 'Swimming' },
    hiking: { icon: 'bi-tree', color: '#198754', label: 'Hiking' },
    walking: { icon: 'bi-person-walking', color: '#6c757d', label: 'Walking' },
    football: { icon: 'bi-trophy', color: '#ffc107', label: 'Football' },
    badminton: { icon: 'bi-circle', color: '#6f42c1', label: 'Badminton' },
    other: { icon: 'bi-activity', color: '#adb5bd', label: 'Other' }
};

const PERIODS = [
    { key: 'week', label: '1W' },
    { key: 'month', label: '1M' },
    { key: 'all', label: 'All' },
];

const activityByTypePropType = PropTypes.objectOf(PropTypes.shape({
    count: PropTypes.number,
    distance: PropTypes.number,
    points: PropTypes.number,
    duration: PropTypes.number
}));

const ActivityTypeStatsCard = ({ activitiesByType, activitiesByTypeMonth, activitiesByTypeWeek }) => {
    const [period, setPeriod] = useState('all');

    const data = period === 'week' ? activitiesByTypeWeek
               : period === 'month' ? activitiesByTypeMonth
               : activitiesByType;

    const totalCount = data ? Object.values(data).reduce((sum, s) => sum + s.count, 0) : 0;
    const sortedTypes = data ? Object.entries(data).sort(([, a], [, b]) => b.count - a.count) : [];

    return (
        <Card className="activity-type-stats-card">
            <Card.Header className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                    <i className="bi bi-bar-chart-fill me-2 text-primary"></i>
                    Activity Breakdown
                </h5>
                <div className="btn-group btn-group-sm" role="group">
                    {PERIODS.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`btn btn-${period === key ? 'primary' : 'outline-secondary'}`}
                            onClick={() => setPeriod(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </Card.Header>
            <Card.Body>
                {sortedTypes.length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">No activities in this period</p>
                ) : (
                    <>
                        <div className="activity-stacked-bar">
                            {sortedTypes.map(([type, stats]) => {
                                const config = ACTIVITY_TYPE_CONFIG[type.toLowerCase()] || ACTIVITY_TYPE_CONFIG.other;
                                const pct = totalCount > 0 ? (stats.count / totalCount) * 100 : 0;
                                return (
                                    <div
                                        key={type}
                                        className="activity-bar-segment"
                                        style={{ width: `${pct}%`, backgroundColor: config.color }}
                                        title={`${config.label}: ${stats.count} (${pct.toFixed(0)}%)`}
                                    />
                                );
                            })}
                        </div>
                        <div className="activity-legend">
                            {sortedTypes.map(([type, stats]) => {
                                const config = ACTIVITY_TYPE_CONFIG[type.toLowerCase()] || ACTIVITY_TYPE_CONFIG.other;
                                const pct = totalCount > 0 ? (stats.count / totalCount) * 100 : 0;
                                return (
                                    <div key={type} className="activity-legend-item">
                                        <div className="activity-legend-label">
                                            <span className="activity-legend-dot" style={{ backgroundColor: config.color }} />
                                            <i className={`bi ${config.icon}`} style={{ color: config.color }} />
                                            <span className="activity-legend-name">{config.label}</span>
                                            <span className="activity-legend-pct text-muted">{pct.toFixed(0)}%</span>
                                        </div>
                                        <div className="activity-legend-stats">
                                            <span><strong>{stats.count}</strong> activities</span>
                                            <span><strong>{stats.distance.toFixed(1)}</strong> km</span>
                                            <span><strong>{Math.floor(stats.duration / 3600)}h {Math.floor((stats.duration % 3600) / 60)}m</strong></span>
                                            <span><strong>{stats.points.toFixed(1)}</strong> pts</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

const ProfileView = ({ profile, isOwnProfile, canEditProfile, onEdit, onPermissionsUpdated }) => {
    return (
        <div className="profile-view">
            {/* Profile Header Card */}
            <Card className="profile-header-card mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={3} className="text-center">
                            <Image
                                src={profile.user.profilePicture || '/default-avatar.png'}
                                roundedCircle
                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                alt={`${profile.user.firstName}'s profile`}
                            />
                        </Col>
                        <Col md={6}>
                            <h2 className="mb-1">{profile.user.firstName} {profile.user.lastName}</h2>
                            <p className="text-muted mb-3">@{profile.user.username}</p>

                            {/* Inline Stats */}
                            <Row className="stats-inline">
                                <Col xs={6} md={3} className="stat-inline-item">
                                    <div className="stat-value">{profile.totalActivities !== undefined && profile.totalActivities !== null ? profile.totalActivities.toFixed(0) : 0}</div>
                                    <div className="stat-label">Activities</div>
                                </Col>
                                <Col xs={6} md={3} className="stat-inline-item">
                                    <div className="stat-value">{profile.totalDistance !== undefined && profile.totalDistance !== null ? profile.totalDistance.toFixed(1) : 0} km</div>
                                    <div className="stat-label">Distance</div>
                                </Col>
                                <Col xs={6} md={3} className="stat-inline-item">
                                    <div className="stat-value">{profile.totalPoints !== undefined && profile.totalPoints !== null ? profile.totalPoints.toFixed(1) : 0}</div>
                                    <div className="stat-label">Points</div>
                                </Col>
                                <Col xs={6} md={3} className="stat-inline-item">
                                    <div className="stat-value">{profile.competitionsWon !== undefined && profile.competitionsWon !== null ? profile.competitionsWon.toFixed(0) : 0}</div>
                                    <div className="stat-label">Wins</div>
                                </Col>
                            </Row>
                        </Col>
                        <Col md={3} className="text-end">
                            {canEditProfile && (
                                <Button variant="primary" onClick={onEdit}>
                                    <i className="bi bi-pencil me-2"></i>
                                    Edit Profile
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Competitions and Activities Grid */}
            <Row>
                {/* Left Column - Competitions */}
                <Col lg={6} className="mb-4">
                    <div className="mb-4">
                        <CompetitionsWonCard wonCompetitions={profile.wonCompetitions} />
                    </div>
                    <div className="mb-4">
                        <CurrentCompetitionsCard currentCompetitions={profile.currentCompetitions} />
                    </div>
                    {stravaEnabled && (
                    <div className="mb-4">
                        <StravaConnectCard isOwnProfile={isOwnProfile} stravaInfo={profile.stravaInfo} />
                    </div>
                    )}
                    <PermissionsCard
                        username={profile.user.username}
                        permissions={profile.user.permissions}
                        onPermissionsUpdated={onPermissionsUpdated}
                    />
                </Col>

                {/* Right Column - Recent Activities & Activity Breakdown */}
                <Col lg={6} className="mb-4">
                    <div className="mb-4">
                        <RecentActivities
                            activities={profile.recentActivities}
                            title="Recent Activities"
                            viewAllLink={`/activities/${profile.user.username}`}
                            showAddButton={false}
                        />
                    </div>
                    <ActivityTypeStatsCard
                        activitiesByType={profile.activitiesByType}
                        activitiesByTypeMonth={profile.activitiesByTypeMonth}
                        activitiesByTypeWeek={profile.activitiesByTypeWeek}
                    />
                </Col>
            </Row>
        </div>
    );
};

ProfileView.propTypes = {
    profile: PropTypes.shape({
        user: PropTypes.shape({
            username: PropTypes.string.isRequired,
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired,
            profilePicture: PropTypes.string,
        }),
        totalActivities: PropTypes.number,
        totalDistance: PropTypes.number,
        totalPoints: PropTypes.number,
        totalDuration: PropTypes.number,
        competitionsWon: PropTypes.number,
        activitiesByType: activityByTypePropType,
        activitiesByTypeMonth: activityByTypePropType,
        activitiesByTypeWeek: activityByTypePropType,
        wonCompetitions: PropTypes.arrayOf(PropTypes.shape({
            competitionId: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            points: PropTypes.number,
            endDate: PropTypes.string
        })),
        currentCompetitions: PropTypes.arrayOf(PropTypes.shape({
            competitionId: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            points: PropTypes.number,
            startDate: PropTypes.string,
            endDate: PropTypes.string
        })),
        recentActivities: PropTypes.arrayOf(PropTypes.shape({
            activityId: PropTypes.string.isRequired,
            activityType: PropTypes.string.isRequired,
            date: PropTypes.string.isRequired,
            distance: PropTypes.number.isRequired,
            duration: PropTypes.number.isRequired,
            points: PropTypes.number.isRequired,
        })),
        stravaInfo: PropTypes.shape({
            connected: PropTypes.bool,
            athleteId: PropTypes.string,
            athleteName: PropTypes.string,
        }),
    }).isRequired,
    isOwnProfile: PropTypes.bool.isRequired,
    canEditProfile: PropTypes.bool.isRequired,
    onEdit: PropTypes.func.isRequired,
    onPermissionsUpdated: PropTypes.func.isRequired,
};

ActivityTypeStatsCard.propTypes = {
    activitiesByType: activityByTypePropType,
    activitiesByTypeMonth: activityByTypePropType,
    activitiesByTypeWeek: activityByTypePropType,
};

StatItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    unit: PropTypes.string
};

export default ProfileView;
